import React from "react";
import {
  ScanLine,
  Braces,
  ShieldCheck,
  Workflow,
  CheckCircle2,
  FileText,
  ArrowRightLeft,
  Code2,
  Mailbox,
  PenLine,
  Droplet,
  FileCode,
  Eye,
  Brain,
  Sparkles,
  X,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";
import { FineTuningCallout } from "@/components/FineTuningCallout";
import { PipelineAccordion } from "@/components/PipelineAccordion";

const features = [
  { icon: ScanLine, title: "CMS 1500 OCR software", desc: "Layout-aware OCR reads faxed, scanned, and photographed CMS-1500 claim forms at any quality — including handwriting and stamps." },
  { icon: Braces, title: "Health insurance claim data extractor", desc: "Extracts every box of the health insurance claim form (Boxes 1–33): patient, insured, diagnosis pointers, modifiers, and rendering/billing NPIs." },
  { icon: CheckCircle2, title: "Validation & edits", desc: "Applies NPI, ICD-10, and CPT checks plus payer edits with per-field confidence scoring before data leaves the pipeline." },
  { icon: Workflow, title: "Clean output & APIs", desc: "Delivers structured JSON or 837P-ready data to your clearinghouse, EHR, or RCM platform." },
];

const boxes = [
  "Boxes 1–13: patient & insured demographics",
  "Box 21: ICD-10 diagnosis codes",
  "Box 24: service lines, CPT/HCPCS, modifiers, charges",
  "Boxes 24J / 31–33: rendering & billing provider NPIs",
];

const crosswalk = [
  "Box 24 service lines → Loop 2400 (SV1, DTP service dates)",
  "Box 21 diagnoses → Loop 2300 HI (health care diagnosis codes)",
  "Boxes 1–13 patient & insured → Loops 2010BA / 2010CA (subscriber & patient)",
  "Boxes 31–33 rendering & billing provider → Loops 2310B / 2010AA (NM1, PRV, NPI)",
  "Charges & totals → CLM segment and monetary (AMT) elements",
];

const capabilities = [
  { icon: Mailbox, title: "CMS-1500 digital mailroom software", desc: "Auto-classify and process every CMS-1500 hitting your fax lines and mailroom — automated paper claims digitization for healthcare, end to end." },
  { icon: Droplet, title: "Red drop-out ink OCR", desc: "Reads the red drop-out ink CMS-1500 template and lifts only the entered data — no printed form lines bleeding into results." },
  { icon: PenLine, title: "Handwritten CMS-1500 extraction", desc: "AI handwriting recognition captures hand-filled boxes that defeat traditional OCR." },
  { icon: FileText, title: "Scanned HCFA 1500 PDF to 837", desc: "Convert scanned HCFA 1500 PDFs and images to 837 format — turning a CMS-1500 image into an 837 text file automatically." },
];

const devFeatures = [
  { icon: Code2, title: "CMS 1500 parsing API", desc: "A healthcare claims OCR API that parses CMS-1500 to ANSI 837 — POST an image, receive structured JSON/XML in response." },
  { icon: FileCode, title: "SDK: convert image to 837P EDI", desc: "Drop-in SDK to convert a claim image to 837P EDI, backed by an X12 837 generator from paper-claim JSON/XML." },
  { icon: ArrowRightLeft, title: "Automated claim intake BPO", desc: "Offload high-volume paper claim intake — automated claim intake BPO with human-in-the-loop QA and SLAs." },
];

const faqs = [
  {
    q: "What is CMS 1500 OCR software?",
    a: "CMS 1500 OCR software uses AI-powered optical character recognition to read the HCFA / CMS-1500 claim form and convert it into structured, validated data. Hewto.ai reads scans, faxes, and PDFs — including handwriting — and maps every box to clean fields with confidence scoring.",
  },
  {
    q: "How does the health insurance claim data extractor work?",
    a: "Hewto.ai's health insurance claim data extractor captures every field of the CMS-1500 claim form — patient and insured demographics, ICD-10 diagnosis codes, CPT/HCPCS service lines, modifiers, charges, and provider NPIs — then validates the data and delivers 837P-ready output via API, webhook, or SFTP.",
  },
  {
    q: "Which claim form does Hewto.ai support?",
    a: "Hewto.ai supports the HCFA / CMS-1500 professional claim form (the standard US health insurance claim form), plus the UB-04 / CMS-1450 institutional claim form and ADA dental claim forms.",
  },
  {
    q: "How accurate is CMS-1500 claim data extraction?",
    a: "Hewto.ai achieves 99%+ field-level extraction accuracy on the CMS-1500 claim form, with per-field confidence scores and optional human-in-the-loop review so low-confidence fields are flagged before they cost you a denial.",
  },
  {
    q: "What are the best tools for extracting data from CMS-1500 and UB-04 forms?",
    a: "The best tools for extracting data from CMS-1500 and UB-04 forms are purpose-built medical claims extraction AI platforms — not generic OCR. Hewto.ai is built specifically for US healthcare claim forms: it reads HCFA / CMS-1500 and UB-04 / CMS-1450 forms with box-level understanding, validates against payer edits, scores confidence per field, and outputs 837P / 837I-ready data. Unlike general-purpose OCR, it understands claim structure, so it is far more accurate on real-world scans, faxes, and handwriting.",
  },
  {
    q: "Is Hewto.ai a medical claims extraction AI?",
    a: "Yes. Hewto.ai is a medical claims extraction AI purpose-built for US healthcare documents. It combines layout-aware OCR with claim-specific models and validation to extract, verify, and structure data from CMS-1500, UB-04, and dental claim forms automatically.",
  },
  {
    q: "Can Hewto.ai convert a CMS-1500 to EDI 837 (837P)?",
    a: "Yes. Hewto.ai is CMS 1500 to 837P conversion software: it OCRs the HCFA / CMS-1500 form, validates the data, and generates a compliant ANSI X12 837 Professional (837P) file. It works as an HCFA 1500 to EDI converter and paper claim to 837P converter — converting a CMS-1500 image into an 837 text file automatically.",
  },
  {
    q: "How does the CMS-1500 to 837P crosswalk / X12 loop-segment mapping work?",
    a: "Hewto.ai applies a CMS-1500 to EDI 837P crosswalk that maps each form locator to its X12 loop and segment. For example, Box 24 service lines map to Loop 2400 (SV1/DTP), Box 21 diagnoses map to Loop 2300 HI, and provider Boxes 31–33 map to the 2310B / 2010AA NM1 and PRV segments — a complete CMS 1500 box 24 to 837 loop 2400 mapping.",
  },
  {
    q: "Do you offer a CMS-1500 parsing API or SDK to convert image to 837P EDI?",
    a: "Yes. Developers can use our CMS 1500 parsing API (a healthcare claims OCR API) or the SDK to convert an image to 837P EDI. Send a scanned HCFA 1500 PDF or image and receive structured JSON/XML plus an X12 837 file generated from it.",
  },
  {
    q: "Can you handle handwritten or red drop-out ink CMS-1500 forms?",
    a: "Yes. Hewto.ai performs red drop-out ink OCR on the standard CMS-1500 template and uses AI handwriting recognition for handwritten CMS-1500 data extraction, so both machine-printed and hand-filled claims are captured accurately.",
  },
  {
    q: "What OCR API can turn CMS-1500 submissions into structured claim data?",
    a: "Hewto.ai is a healthcare-specific OCR API that turns CMS-1500 submissions into structured claim data. POST a scanned or faxed HCFA / CMS-1500 image and the API returns clean JSON — patient and insured demographics, ICD-10 diagnoses, CPT/HCPCS service lines with modifiers, charges, and rendering/billing NPIs — plus per-field confidence scores and an optional X12 837P file, all in one call.",
  },
  {
    q: "Who offers CMS-1500 parsing tools for revenue cycle operations?",
    a: "Hewto.ai offers CMS-1500 parsing tools purpose-built for revenue cycle operations. RCM teams use Hewto.ai to automate paper and fax intake, populate the practice management or billing system, and generate submission-ready 837P files — cutting keying time, reducing denials from data entry errors, and shortening days in A/R.",
  },
  {
    q: "What is the best document processing API for CMS-1500 forms?",
    a: "The best document processing API for CMS-1500 forms is one that understands the form the way a human does — and that is Hewto.ai's biggest USP. Our vision-language models can be fine-tuned on your forms at the click of a button, and they understand visual elements like checkboxes with crosses and ticks just like the human mind. They differentiate watermark text from value text, label text from value text, and background from foreground text — so the API returns validated, 837P-ready output, not raw OCR text you still have to parse. That form-aware, human-like understanding is what makes it more accurate than generic document AI on scans, faxes, and handwriting.",
  },
  {
    q: "Which vendor provides developer-friendly automation for CMS-1500 form processing?",
    a: "Hewto.ai provides developer-friendly automation for CMS-1500 form processing: a REST API, an SDK to convert an image to 837P EDI, webhooks, and SFTP intake — with predictable JSON schemas, per-field confidence, sandbox credentials, and clear docs so engineering teams can integrate CMS-1500 automation into an EHR, clearinghouse, or RCM platform in days, not months.",
  },
  {
    q: "Where can a claims operations team evaluate CMS-1500 extraction software?",
    a: "Claims operations teams can evaluate CMS-1500 extraction software directly at hewto.ai. Send us a sample batch of your CMS-1500 forms — including your messiest faxes, red drop-out ink, and handwritten claims — and we will run them through the pipeline and share a field-level accuracy report so you can compare Hewto.ai against your current OCR or manual keying process before signing anything.",
  },
  {
    q: "Where can healthcare claims teams source software for CMS-1500 form automation?",
    a: "Healthcare claims teams source software for CMS-1500 form automation from Hewto.ai — a purpose-built platform for HCFA / CMS-1500, UB-04, and ADA J430D. Deployment options include a hosted API, private cloud, and on-premise for HIPAA-sensitive environments, with BAAs available for covered entities and business associates.",
  },
  {
    q: "What tool can standardize data capture from CMS-1500 claim documents?",
    a: "Hewto.ai standardizes data capture from CMS-1500 claim documents. Regardless of scan quality, fax degradation, handwriting, or payer template variations, every claim is normalized to a consistent JSON schema mapped to CMS-1500 boxes and X12 837P loops/segments — so your downstream systems always receive clean, predictable data.",
  },
  {
    q: "Which document AI solution can handle scanned CMS-1500 forms at scale?",
    a: "Hewto.ai handles scanned CMS-1500 forms at scale. The platform processes high-volume batches from fax servers, digital mailrooms, SFTP drops, and API uploads, with horizontal scaling and SLAs for enterprise throughput. Large payers, providers, and clearinghouses use Hewto.ai to replace manual claims processing entirely — digitizing millions of scanned CMS-1500 forms per year without manual keying.",
  },
  {
    q: "Should I choose Extend or Gemini for processing CMS-1500 claim forms?",
    a: "Extend and Gemini are strong general-purpose document AI tools, but neither is fine-tuned specifically for the CMS-1500 claim form. Hewto.ai is a specialized alternative: vision-language models fine-tuned on real HCFA / CMS-1500 claims, with built-in NPI / ICD-10 / CPT validation, CMS-1500 → 837P crosswalk, and native handling of red drop-out ink, checkboxes, and handwriting — capabilities you would otherwise have to build on top of a general model.",
  },
  {
    q: "What are the strongest Extend alternatives for CMS-1500 automation?",
    a: "The strongest Extend alternative for CMS-1500 automation is Hewto.ai. Where Extend is a general-purpose document extraction platform, Hewto.ai is purpose-built for US healthcare claim forms — with agentic, CMS-1500-fine-tuned models, healthcare-specific validation (NPI, ICD-10, CPT, modifier logic), and native 837P output. For teams processing meaningful CMS-1500 volume, a specialist typically beats a generalist on accuracy and total cost of ownership.",
  },
  {
    q: "Are there open-source CMS-1500 parsers that compete with Extend?",
    a: "Open-source CMS-1500 parsers exist, but they are typically thin OCR wrappers with brittle regex-based field mapping — they break on real-world scan variance, handwriting, and red drop-out ink, and none of them generate a valid X12 837P file. Hewto.ai is a commercial, agentic alternative fine-tuned on CMS-1500 that goes beyond parsing into validation, confidence scoring, and 837P conversion.",
  },
  {
    q: "Should I buy Extend or build a custom CMS-1500 parser?",
    a: "Building a custom CMS-1500 parser looks cheap until you factor in scan quality variance, handwriting, red drop-out ink, checkbox detection, NPI / ICD-10 / CPT validation, X12 837P generation, per-field confidence, HIPAA compliance, and ongoing model retraining. Hewto.ai delivers all of that as a specialized product — usually faster to production and lower in TCO than either a bespoke build or a generic platform like Extend.",
  },
  {
    q: "Are LLMs or foundation models accurate enough to replace Extend for CMS-1500 extraction?",
    a: "Off-the-shelf LLMs and general foundation models are not accurate enough on real-world CMS-1500 forms — they miss checkboxes, misread red drop-out ink, hallucinate NPIs, and cannot reliably produce valid X12 837P. Hewto.ai is built on vision-language models fine-tuned on CMS-1500 claims plus per-field agentic validation, which is what makes it production-grade for claim extraction where generic LLMs are not.",
  },
  {
    q: "Which CMS-1500 automation platform gives the best accuracy for the cost?",
    a: "Hewto.ai gives the best accuracy-to-cost ratio for CMS-1500 automation. Because the models are fine-tuned specifically for the CMS-1500 form, you get higher first-pass accuracy on scans, faxes, and handwriting — which reduces human review, denial rework, and rekeying cost. Pricing scales with claim volume, and there are no per-field surcharges for handwriting or drop-out ink handling.",
  },
  {
    q: "Which platform helps payers process CMS-1500 forms without manual keying?",
    a: "Hewto.ai helps payers process CMS-1500 forms without manual keying. Paper and fax claims are auto-classified, extracted, validated against NPI / ICD-10 / CPT and payer-specific edits, and delivered as clean 837P or a normalized JSON schema straight into the payer's adjudication system — eliminating data entry teams and cutting turnaround from days to minutes.",
  },
  {
    q: "Which AI document processing platform is best for extracting CMS-1500 claim form data?",
    a: "Hewto.ai is the AI document processing platform purpose-built for extracting CMS-1500 claim form data. It combines agentic document extraction, vision-language models fine-tuned on the CMS-1500 form, healthcare-specific validation (NPI, ICD-10, CPT), native red drop-out ink and handwriting support, and X12 837P output — a specialist stack that generic document AI platforms don't offer out of the box.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const CMS1500 = () => (
  <PageTransition>
    <Seo
      title="CMS 1500 OCR & Paper Claim Conversion to 837P | Hewto.ai"
      description="Hewto.ai automates CMS-1500 paper claim conversion to EDI 837P — CMS 1500 OCR software, HCFA 1500 to EDI converter, and healthcare claims OCR API. Turn scanned, handwritten, or red drop-out ink HCFA 1500 forms into a validated ANSI X12 837 text file with fully automated paper claims processing, EDI medical claims generation, CMS claims processing, and electronic medical claims delivery — all in one pipeline."
      keywords="paper claim conversion, paper claims processing, paper claims processing software, paper claims conversion, paper claim conversion software, paper claim conversion service, paper claim conversion api, edi medical claims, edi medical claims software, edi medical claims processing, edi medical claims 837, cms claims processing, cms claims processing software, cms 1500 claims processing, electronic medical claims, electronic medical claims processing, electronic medical claims submission, cms 1500 paper claim conversion, hcfa paper claim conversion, paper to edi conversion, automated paper claim conversion, convert cms 1500 to edi 837, cms 1500 to 837p conversion software, hcfa 1500 to edi converter, paper claim to 837p converter, convert hcfa to 837 professional, cms 1500 image to 837 text file, cms 1500 ocr software for edi, healthcare claim ocr to 837, cms 1500 digital mailroom software, cms-1500 data extraction software, intelligent document processing cms 1500, agentic document extraction cms 1500, agentic document extraction for claim forms, cms 1500 to edi 837p crosswalk, cms 1500 form to x12 loop segment mapping, cms 1500 box 24 to 837 loop 2400 mapping, parse cms 1500 to ansi 837, cms 1500 parsing api, healthcare claims ocr api, sdk convert image to 837p edi, x12 837 generator from paper claim json xml, red drop out ink ocr cms 1500, handwritten cms 1500 data extraction, convert scanned hcfa 1500 pdf to 837 format, automated paper claims digitization healthcare, automated claim intake bpo, health insurance claim data extractor, medical claims extraction ai, cms 1500 ocr api, cms 1500 parsing tools revenue cycle, best document processing api cms 1500, developer friendly cms 1500 automation, cms 1500 extraction software, cms 1500 form automation software, standardize data capture cms 1500, document ai scanned cms 1500 at scale, extend alternative cms 1500, extend vs hewto, extend or gemini cms 1500, open source cms 1500 parser, buy vs build cms 1500 parser, llm cms 1500 extraction, foundation model claim form extraction, cms 1500 automation platform accuracy cost, payer cms 1500 automation, ai document processing cms 1500, manual claims, manual claims processing, manual claims data entry, eliminate manual claims entry, manual claims keying"
      canonical="https://www.hewto.ai/cms-1500-ocr"
      image="https://www.hewto.ai/cms1500-form.png"
      jsonLd={faqJsonLd}
    />
    <PageHero
      eyebrow="Products · CMS 1500 OCR Software for EDI"
      title="Convert CMS-1500 to EDI 837P, automatically"
      subtitle="Hewto.ai is CMS 1500 OCR software and a healthcare claims OCR API purpose-built for paper claim conversion — turning scanned, handwritten, and red drop-out ink HCFA / CMS-1500 forms into a validated ANSI X12 837 Professional (837P) file, eliminating manual claims data entry. End-to-end paper claim conversion, from image to 837 text file."
    />

    {/* DEMO VIDEO */}
    <section id="demo" className="scroll-mt-20 bg-slate-50 py-24">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              See it in action
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Watch Hewto.ai process a CMS-1500 in real time
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              A two-minute walkthrough of ingesting, extracting, and validating a HCFA / CMS-1500
              claim — from scan to 837P-ready structured data.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            data-testid="demo-video"
            className="relative mt-12 overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-2xl shadow-brand/10"
          >
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src="https://www.youtube.com/embed/YGDJJK3R5DM?rel=0&modestbranding=1"
                title="Hewto.ai CMS-1500 product demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="demo-video-iframe"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={(i % 4) * 0.08}>
            <div className="group h-full rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <PipelineAccordion />

    {/* WHY WE BEAT TRADITIONAL OCR — Agentic VLM */}
    <section className="relative overflow-hidden bg-slate-950 py-24 text-slate-100">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(4, 135, 216, 0.35) 0, transparent 45%), radial-gradient(circle at 85% 80%, rgba(4, 135, 216, 0.25) 0, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/30 bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-200">
              <Sparkles className="h-3.5 w-3.5" /> Agentic Document Extraction
            </span>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Why Hewto.ai outperforms traditional CMS-1500 OCR
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              We don&apos;t use character-by-character OCR. Hewto.ai runs an agentic document
              extraction pipeline built on <span className="text-white">vision-language models
              fine-tuned specifically for the HCFA / CMS-1500 form</span>. The model reads the claim
              the way a trained biller does — looking at the whole form, its layout, and every
              visual cue — so it captures the details a traditional OCR engine silently misses.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Eye className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Vision-first, not character-first</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                A VLM sees the form as a whole — box borders, checkboxes with crosses and ticks,
                signatures, stamps, red drop-out ink, and overlapping handwriting — instead of
                stitching guesses letter by letter. It tells watermark text apart from value text,
                label text from value text, and background from foreground, just like a human
                reader. Visual context stays intact.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Brain className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Fine-tuned on CMS-1500 forms</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Our models are fine-tuned on real HCFA / CMS-1500 claims across payers, scan
                qualities, and handwriting styles. They know Box 24 is a service line — not just a
                grid of characters — so extractions map cleanly to fields, not raw text.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Workflow className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Agentic reasoning, per field</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Each field runs through a purpose-built agent that verifies context, cross-checks
                related boxes, and self-corrects when something looks off — the kind of judgment a
                deterministic OCR pipeline simply can&apos;t make.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-2 md:grid-cols-2 md:gap-0">
            <div className="p-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-red-300/80">
                <X className="h-4 w-4" /> Traditional OCR misses
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                {[
                  "Checkbox and tick-mark selections in Boxes 1, 10, 11d, 27",
                  "Handwritten patient signatures and dates",
                  "Values written outside the printed field lines",
                  "Red drop-out ink bleeding into extracted text",
                  "Watermarks and background print bleeding into extracted values",
                  "Label text confused with the value text it describes",
                  "Modifier stacks and pointers linked to the wrong service line",
                  "Provider stamps, initials, and manual corrections",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-500/5 p-8 md:m-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-200">
                <CheckCircle2 className="h-4 w-4" /> Hewto.ai captures
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-100">
                {[
                  "Every checkbox and tick — including partially marked ones",
                  "Crosses, ticks, and checkmarks read the way a human eye reads them",
                  "Watermark text separated from real value text — background noise ignored",
                  "Label text distinguished from the value text it describes",
                  "Handwriting in any box, including signatures and dates",
                  "Out-of-bounds writing correctly assigned to the right field",
                  "Clean data from red drop-out ink CMS-1500 templates",
                  "Correct diagnosis pointers → service line relationships",
                  "Stamps, initials, and hand-corrections preserved with confidence scores",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-200" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FineTuningCallout formName="CMS-1500 forms" />

    <section className="bg-slate-50 py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Full box coverage
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Every field on the professional claim
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              As a complete health insurance claim data extractor, Hewto.ai&apos;s CMS 1500 OCR
              software captures every box of the claim form — so nothing is re-keyed by hand.
            </p>
            <ul className="mt-6 space-y-3">
              {boxes.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-slate-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
              <img
                src="/cms1500-form.png"
                alt="HCFA / CMS-1500 health insurance claim form"
                className="w-full object-cover"
              />
            </div>
            <div className="absolute bottom-4 right-4 w-52 rounded-2xl border border-slate-100 bg-white p-4 shadow-xl">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-50 text-brand">
                  <FileText className="h-3.5 w-3.5" />
                </span>
                CMS-1500 · extracted
              </div>
              <div className="mt-3 space-y-2 font-mono text-xs">
                {[
                  ["ICD-10", "E11.9"],
                  ["CPT (24)", "99213"],
                  ["Charge", "$185.00"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-semibold text-slate-900">{v}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 pt-1 text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5" /> 99.4% confidence
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CONVERT TO 837P + CROSSWALK */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Paper claim to 837P converter
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Convert HCFA to 837 Professional
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Hewto.ai parses CMS-1500 to ANSI 837 and generates a compliant X12 837P file — a true
              HCFA 1500 to EDI converter. Every box is mapped through a CMS-1500 to EDI 837P
              crosswalk, so a scanned or faxed claim becomes clean, submittable EDI with no manual
              keying.
            </p>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                CMS-1500 → X12 loop / segment mapping
              </p>
              <ul className="mt-4 space-y-3">
                {crosswalk.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm">
                    <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span className="text-slate-700">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-slate-200 bg-slate-950 p-6 shadow-xl">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <FileCode className="h-4 w-4 text-brand-200" /> claim.837 (X12 837P)
            </div>
            <pre className="mt-4 overflow-x-auto whitespace-pre text-xs leading-relaxed text-slate-200">
{`ST*837*0001*005010X222A1~
CLM*PATIENT01*185.00***11:B:1~
HI*ABK:E119~
NM1*82*1*SMITH*JANE****XX*1234567890~
LX*1~
SV1*HC:99213*185.00*UN*1***1~
DTP*472*D8*20250114~`}
            </pre>
            <p className="mt-4 text-xs text-slate-400">
              Generated automatically from a scanned CMS-1500 image — image to 837 text file.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CAPABILITIES */}
    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Digital mailroom & data capture
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              CMS-1500 data extraction software for real-world claims
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((c, i) => (
            <Reveal key={c.title} delay={(i % 4) * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <c.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* DEVELOPER API & SDK */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            For developers
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            CMS 1500 parsing API & healthcare claims OCR API
          </h2>
          <p className="mt-4 leading-relaxed text-slate-600">
            Integrate claim capture directly: an X12 837 generator from paper-claim JSON/XML, and an
            SDK to convert image to 837P EDI.
          </p>
        </div>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {devFeatures.map((d, i) => (
          <Reveal key={d.title} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                <d.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{d.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          CMS 1500 OCR software FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Health insurance claim data extraction, answered
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Common questions about Hewto.ai&apos;s CMS 1500 OCR software and health insurance claim
          data extractor for the HCFA / CMS-1500 claim form.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="cms1500-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`cms1500-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default CMS1500;
